import { use, useState } from "react";
import clsx from "clsx";

import { useBookmarks } from "lib/bookmarks/use-bookmarks";
import { useChangedChapters } from "lib/drafts/use-changed-chapters";
import { getConfig } from "lib/get-config";
import { useTranslation } from "lib/i18n/use-translation";
import { ContentLayoutContext } from "lib/layout/use-content-layout";
import { NavigationContext } from "lib/navigation/use-navigation";
import { usePaths } from "lib/navigation/use-paths";
import { useQuotes } from "lib/quotes/use-quotes";
import { useToc } from "lib/toc/use-toc";
import { Chapter, TocItem as TocItemType, Xpointer } from "lib/types";
import {
  Dialog,
  DialogOverlay,
  DialogPanel,
  DialogTrigger,
} from "lib/ui/dialog";

import BookmarkFilledIcon from "assets/icons/bookmark-filled.svg?react";
import QuoteIcon from "assets/icons/quote.svg?react";
import TocIcon from "assets/icons/toc.svg?react";
import ChapterChangedIcon from "assets/icons/update.svg?react";
import { DraftInfo } from "./draft-info";
import css from "./toc.module.scss";

interface TocItemProps {
  item: TocItemType | Chapter;
  level?: number;
  onChapterClick: (index: number) => void;
  currentPage: number;
  activeChapter: Chapter | null;
  bookmarkCount?: number;
  quoteCount?: number;
  firstAvailableIndex: number | null;
  lastAvailableIndex: number | null;
  changedXpointers: Set<Xpointer>;
}

function TocItem({
  item,
  level = 0,
  onChapterClick,
  currentPage,
  activeChapter,
  bookmarkCount = 0,
  quoteCount = 0,
  firstAvailableIndex,
  lastAvailableIndex,
  changedXpointers,
}: TocItemProps) {
  const { t } = useTranslation();
  const { indexToPage, indexToXpointer } = usePaths();
  const afterFirst =
    firstAvailableIndex === null || item.s >= firstAvailableIndex;
  const beforeLast =
    lastAvailableIndex === null || item.s <= lastAvailableIndex;
  const isAvailable = afterFirst && beforeLast;
  const isClickable = Boolean(item.s && item.t) && isAvailable;

  const page = item.s !== undefined ? indexToPage(item.s) : null;
  const pageNumber = page !== null ? page + 1 : null;

  const isActive = activeChapter === item;

  const xpointer = indexToXpointer(item.s);
  const isChanged = xpointer ? changedXpointers.has(xpointer) : false;

  return (
    <div className={css.tocItem}>
      <div className={css.tocRowGroup}>
        <div className={clsx(css.tocRow, !isAvailable && css.unavailable)}>
          <div className={css.leftContent}>
            {bookmarkCount > 0 && (
              <span className={css.counter} data-testid="tocBookmarkCounter">
                <BookmarkFilledIcon />
                {bookmarkCount}
              </span>
            )}
            {quoteCount > 0 && (
              <span className={css.counter} data-testid="tocQuoteCounter">
                <QuoteIcon />
                {quoteCount}
              </span>
            )}
            {isClickable ? (
              <button
                onClick={() => onChapterClick(item.s)}
                className={clsx(css.tocLabel, isActive && css.active)}
                data-testid="tocItem"
              >
                {item.t}
              </button>
            ) : (
              <span
                className={clsx(css.tocLabel, isActive && css.active)}
                data-testid="tocItem"
              >
                {item.t}
              </span>
            )}
          </div>
          {pageNumber !== null && (
            <span
              className={clsx(css.pageNumber, isActive && css.activePageNumber)}
              data-testid="tocPageNumber"
            >
              {pageNumber}
            </span>
          )}
        </div>
        {isChanged && (
          <div className={css.changedInformer} data-testid="tocChangedInformer">
            <ChapterChangedIcon />
            <span>{t("Автор внёс изменения в эту главу")}</span>
          </div>
        )}
      </div>
      {item.c && item.c.length > 0 && (
        <div className={css.children}>
          {item.c
            .filter(child => child.t)
            .map(child => (
              <TocItem
                key={JSON.stringify(child)}
                item={child}
                level={level + 1}
                onChapterClick={onChapterClick}
                currentPage={currentPage}
                activeChapter={activeChapter}
                firstAvailableIndex={firstAvailableIndex}
                lastAvailableIndex={lastAvailableIndex}
                changedXpointers={changedXpointers}
              />
            ))}
        </div>
      )}
    </div>
  );
}

export function Toc() {
  const { updateBookmark, page, isLayoutComplete } = use(NavigationContext)!;
  const { toc } = useToc();
  const [isOpen, setIsOpen] = useState(false);
  const { bookmarks } = useBookmarks();
  const { quotes } = useQuotes();
  const { indexToXpointer, xpointerToIndex, indexToPage } = usePaths();
  const { trials } = getConfig();

  const { t } = useTranslation();

  const { layout } = use(ContentLayoutContext)!;

  const isFragment = trials === 1;
  const fragmentLastIndex =
    isFragment && toc?.Parts?.length ? toc.Parts[toc.Parts.length - 1].e : null;

  const layoutIndices =
    !isLayoutComplete && layout?.elements
      ? Object.keys(layout.elements).map(Number)
      : null;
  const layoutFirstIndex = layoutIndices ? Math.min(...layoutIndices) : null;
  const layoutLastIndex = layoutIndices ? Math.max(...layoutIndices) : null;

  const lastAvailableIndex = (() => {
    if (fragmentLastIndex !== null && layoutLastIndex !== null) {
      return Math.min(fragmentLastIndex, layoutLastIndex);
    }
    return fragmentLastIndex ?? layoutLastIndex;
  })();

  const label = t("Оглавление");

  function countItemsInChapter(
    chapter: Chapter,
    items: Array<{ xpointer: string } | { start_xpointer: string }> | undefined
  ) {
    if (!chapter.s || !chapter.e || !items) return 0;

    const count = items.filter(item => {
      const xpointer = "xpointer" in item ? item.xpointer : item.start_xpointer;
      const index = xpointerToIndex(xpointer);

      if (index === null) return false;

      return index >= chapter.s && index <= chapter.e;
    }).length;

    return count;
  }

  function findActiveChapter(
    chapters: Chapter[],
    currentPage: number
  ): Chapter | null {
    let mostSpecific: Chapter | null = null;

    function checkChapter(chapter: Chapter): void {
      if (
        !chapter.s ||
        chapter.s === undefined ||
        !chapter.e ||
        chapter.e === undefined
      )
        return;

      const startPage = indexToPage(chapter.s);
      const endPage = indexToPage(chapter.e);

      if (startPage === null || endPage === null) return;

      if (currentPage >= startPage + 1 && currentPage <= endPage + 1) {
        mostSpecific = chapter;

        if (chapter.c) {
          for (const child of chapter.c) {
            checkChapter(child);
          }
        }
      }
    }

    for (const chapter of chapters) {
      checkChapter(chapter);
    }

    return mostSpecific;
  }

  const activeChapter = toc?.Body?.[0]?.c
    ? findActiveChapter(toc.Body[0].c, page + 1)
    : null;

  const changedXpointers = useChangedChapters(Boolean(toc.Meta?.DraftStatus));

  const fullLength = toc["fb3-fragment"]?.full_length ?? toc.full_length;

  function handleChapterClick(index: number) {
    const xpointer = indexToXpointer(index);

    if (!xpointer) {
      console.warn("[TOC] Could not find xpointer for index", index);
      setIsOpen(false);
      return;
    }

    updateBookmark(xpointer);
    setIsOpen(false);
  }

  return (
    <Dialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      triggerLabel={label}
      panelLabel={{ labelledBy: "tocTitle" }}
    >
      <DialogTrigger
        className={clsx(css.trigger, isOpen && css.active)}
        data-testid="tocButton"
      >
        <TocIcon />
      </DialogTrigger>
      <DialogOverlay className={css.overlay} />
      <DialogPanel
        className={clsx(css.drawer, isOpen && css.open)}
        data-testid="tocDrawer"
      >
        <h3 id="tocTitle" className={css.title}>
          {label}
        </h3>
        <div className={css.tocContent}>
          {toc?.Body?.[0]?.c
            ?.filter((item: Chapter) => item.t)
            ?.map((item: Chapter) => (
              <TocItem
                key={JSON.stringify(item)}
                item={item}
                onChapterClick={handleChapterClick}
                currentPage={page + 1}
                activeChapter={activeChapter}
                bookmarkCount={countItemsInChapter(item, bookmarks)}
                quoteCount={countItemsInChapter(item, quotes)}
                firstAvailableIndex={layoutFirstIndex}
                lastAvailableIndex={lastAvailableIndex}
                changedXpointers={changedXpointers}
              />
            ))}
        </div>
        {toc.Meta.DraftStatus && (
          <DraftInfo
            draftStatus={toc.Meta.DraftStatus}
            fullLength={fullLength}
            updated={toc.Meta.Updated}
            created={toc.Meta.Created}
          />
        )}
      </DialogPanel>
    </Dialog>
  );
}
